/**
 * Vehicle Form Component
 * Task 58: Form for creating/editing vehicles with plate, make, model, year, VIN
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { IndustryVehicle } from '@/types/industry';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const vehicleSchema = z.object({
  client_id: z.coerce.number().min(1, 'Cliente é obrigatório'),
  plate: z.string().min(1, 'Placa é obrigatória'),
  make: z.string().min(1, 'Marca é obrigatória'),
  model: z.string().min(1, 'Modelo é obrigatório'),
  year: z.coerce.number().min(1900).max(new Date().getFullYear() + 1),
  vin: z.string().optional(),
  color: z.string().optional(),
  mileage: z.coerce.number().min(0).optional(),
});

type VehicleFormData = z.infer<typeof vehicleSchema>;

interface VehicleFormProps {
  vehicle?: IndustryVehicle;
  clients: Array<{ id: number; name: string }>;
  onSubmit: (data: VehicleFormData) => void | Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

// Common car makes for Brazil
const COMMON_MAKES = [
  'Chevrolet',
  'Fiat',
  'Ford',
  'Honda',
  'Hyundai',
  'Jeep',
  'Nissan',
  'Peugeot',
  'Renault',
  'Toyota',
  'Volkswagen',
  'Outros',
];

export function VehicleForm({ vehicle, clients, onSubmit, onCancel, isSubmitting = false }: VehicleFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: vehicle
      ? {
          client_id: vehicle.client_id!,
          plate: (vehicle.metadata?.plate as string) || '',
          make: (vehicle.metadata?.make as string) || '',
          model: (vehicle.metadata?.model as string) || '',
          year: (vehicle.metadata?.year as number) || new Date().getFullYear(),
          vin: (vehicle.metadata?.vin as string) || '',
          color: (vehicle.metadata?.color as string) || '',
          mileage: (vehicle.metadata?.mileage as number) || undefined,
        }
      : {
          year: new Date().getFullYear(),
        },
  });

  const selectedMake = watch('make');
  const selectedClient = watch('client_id');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        {/* Client and Plate */}
        <Card>
          <CardHeader>
            <CardTitle>Identificação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select
                  value={selectedClient?.toString()}
                  onValueChange={(v) => setValue('client_id', parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.client_id && (
                  <p className="text-sm text-destructive">{errors.client_id.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Placa *</Label>
                <Input
                  {...register('plate')}
                  placeholder="ABC-1234 ou ABC1D23"
                  className="font-mono uppercase"
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase();
                    register('plate').onChange(e);
                  }}
                />
                {errors.plate && <p className="text-sm text-destructive">{errors.plate.message}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Details */}
        <Card>
          <CardHeader>
            <CardTitle>Dados do Veículo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Marca *</Label>
                <Select value={selectedMake} onValueChange={(v) => setValue('make', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a marca" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_MAKES.map((make) => (
                      <SelectItem key={make} value={make}>
                        {make}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!selectedMake && (
                  <Input
                    {...register('make')}
                    placeholder="Ou digite a marca"
                    className="mt-2"
                  />
                )}
                {errors.make && <p className="text-sm text-destructive">{errors.make.message}</p>}
              </div>

              <div className="space-y-2">
                <Label>Modelo *</Label>
                <Input {...register('model')} placeholder="Ex: Civic, Onix, Gol" />
                {errors.model && <p className="text-sm text-destructive">{errors.model.message}</p>}
              </div>

              <div className="space-y-2">
                <Label>Ano *</Label>
                <Input type="number" {...register('year')} placeholder="Ex: 2023" />
                {errors.year && <p className="text-sm text-destructive">{errors.year.message}</p>}
              </div>

              <div className="space-y-2">
                <Label>Cor</Label>
                <Input {...register('color')} placeholder="Ex: Prata, Branco" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>VIN/Chassi</Label>
                <Input
                  {...register('vin')}
                  placeholder="17 caracteres"
                  maxLength={17}
                  className="font-mono uppercase"
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase();
                    register('vin').onChange(e);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Número de identificação do veículo (opcional)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Quilometragem</Label>
                <Input type="number" {...register('mileage')} placeholder="Ex: 50000" />
                <p className="text-xs text-muted-foreground">
                  Quilometragem atual do veículo
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : vehicle ? 'Atualizar' : 'Cadastrar'} Veículo
          </Button>
        </div>
      </div>
    </form>
  );
}
